/* ============================================================
   The operator profile — one shared model of one business.

   Fourteen tools and, until now, zero of them knew anything the others
   had learned. An operator costing the Mombasa–Nairobi corridor entered
   the corridor, the distance and the payload; then entered the same
   three again in Route Profitability; then a third time in Corridor
   Emissions. Landed Cost computed the duty payable and the days the box
   sat, which is precisely the duty float the Financeability Pack asks
   for — and asked for it again, blank.

   The tools already compute each other's inputs. This is the record
   that lets them.

   THREE RULES, and they are the whole design.

   1. NEVER OVERWRITE WHAT SOMEONE TYPED. A prefill fills a blank field
      and nothing else. If the user has put a number in a box, that
      number is theirs and it stands, even when the profile disagrees.

   2. NEVER PREFILL SILENTLY. A figure that appears in a form without
      the user putting it there is an assumption, and an unnoticed
      assumption is how someone tenders against a stale payload. Every
      prefill is announced and every announcement is dismissible.

   3. NEVER OUTLIVE ITS USEFULNESS. A corridor entered four months ago
      is not a fact about today. The profile carries the date each field
      was learned and stops offering anything past MAX_AGE_DAYS rather
      than presenting a stale number as current.

   It stays on the device like everything else here, it is listed in
   Saved work, and it is erased by the same button that erases the rest.
   A store the user cannot see or destroy would break the only promise
   this product actually makes.
   ============================================================ */

import { session } from './storage.js';

const STORAGE_KEY = 'kanda_profile';

/**
 * Past this, a remembered figure is offered no longer. Corridor rates,
 * payloads and payment terms all move inside a quarter; ninety days is
 * long enough to span a normal gap between engagements and short enough
 * that nothing here silently becomes last season's number.
 */
export const MAX_AGE_DAYS = 90;

/**
 * Every field the profile will carry, and — the important column — the
 * label used when telling the user it was reused. Anything not listed
 * here is not remembered, which keeps the record to facts about the
 * operation rather than an accreting log of everything ever typed.
 */
export const FIELDS = {
  corridor: { label: 'corridor' },
  mode: { label: 'mode' },
  distance: { label: 'distance', unit: 'km' },
  payload: { label: 'payload', unit: 't' },
  containers: { label: 'containers' },
  freeDays: { label: 'free days' },
  tripsPerMonth: { label: 'trips per month' },
  annualRevenue: { label: 'annual revenue' },
  dutyFloatDays: { label: 'duty float', unit: 'days' },
  dso: { label: 'debtor days' },
  dpo: { label: 'creditor days' },
  fleetAge: { label: 'average fleet age', unit: 'yr' },
  topClientShare: { label: 'largest client share', unit: '%' }
};

const isKnownField = (key) => Object.prototype.hasOwnProperty.call(FIELDS, key);

/** A value worth remembering: present, and not a placeholder zero. */
function isMeaningful(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0;
  return true;
}

function read() {
  const raw = session.get(STORAGE_KEY);
  return raw && typeof raw === 'object' ? raw : {};
}

function ageInDays(iso, now) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return Infinity;
  return (now.getTime() - then.getTime()) / 86400000;
}

/**
 * Everything still fresh enough to offer.
 *
 * @returns {Object<string, {value: *, learnedAt: string, ageDays: number}>}
 */
export function getProfile(now = new Date()) {
  const stored = read();
  const live = {};

  for (const [key, entry] of Object.entries(stored)) {
    if (!isKnownField(key) || !entry || typeof entry !== 'object') continue;
    const ageDays = ageInDays(entry.learnedAt, now);
    if (ageDays > MAX_AGE_DAYS) continue;
    live[key] = { ...entry, ageDays };
  }

  return live;
}

/**
 * Record what a tool just learned. Only known, meaningful fields are
 * kept, and each carries the moment it was learned so staleness is a
 * property of the field rather than of the record as a whole — a
 * corridor from March should not be kept alive by a payload from today.
 */
export function rememberProfile(patch, now = new Date()) {
  if (!patch || typeof patch !== 'object') return;

  const stored = read();
  let changed = false;

  for (const [key, value] of Object.entries(patch)) {
    if (!isKnownField(key) || !isMeaningful(value)) continue;
    stored[key] = { value, learnedAt: now.toISOString() };
    changed = true;
  }

  if (changed) session.set(STORAGE_KEY, stored);
}

export function clearProfile() {
  session.remove(STORAGE_KEY);
}

/** For Saved work, which must be able to show and erase everything. */
export function profileSummary(now = new Date()) {
  const live = getProfile(now);
  const entries = Object.entries(live);
  if (entries.length === 0) return null;

  const newest = entries.reduce((min, [, e]) => Math.min(min, e.ageDays), Infinity);
  return {
    count: entries.length,
    fields: entries.map(([key, entry]) => ({
      key,
      label: FIELDS[key].label,
      unit: FIELDS[key].unit || '',
      value: entry.value
    })),
    ageDays: newest
  };
}

/* ============================================================
   Applying it to a form
   ============================================================ */

/**
 * Fill blank inputs from the profile and report what was touched.
 *
 * Blank is the whole test — `input.value === ''`. A field the user has
 * typed into, or one the tool has given a sensible default, is left
 * exactly as it is. That is rule one, and it is why this returns a list
 * rather than doing anything clever: the caller has to say what it did.
 *
 * @param {HTMLElement} root      the form
 * @param {Object<string,string>} map  profile field -> element id
 * @returns {Array<{key, label, value, unit, ageDays}>} what was prefilled
 */
export function prefillForm(root, map, now = new Date()) {
  const live = getProfile(now);
  const applied = [];

  for (const [key, elementId] of Object.entries(map)) {
    const entry = live[key];
    if (!entry) continue;

    const el = root.querySelector(`#${elementId}`);
    if (!el || el.value !== '') continue;

    // A <select> can only take a value it actually offers. Setting an
    // absent option silently blanks the control, which would be worse
    // than not prefilling at all.
    if (el.tagName === 'SELECT') {
      const options = [...el.options].map((o) => o.value);
      if (!options.includes(String(entry.value))) continue;
    }

    el.value = String(entry.value);
    applied.push({
      key,
      elementId,
      label: FIELDS[key].label,
      unit: FIELDS[key].unit || '',
      value: entry.value,
      ageDays: entry.ageDays
    });
  }

  return applied;
}

/** "3 days ago", "today" — for the prefill notice. */
export function describeAge(ageDays) {
  const days = Math.floor(ageDays);
  if (days <= 0) return 'earlier today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
}
